import React, {useContext, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Edit from "@material-ui/icons/Edit";
import Clear from "@material-ui/icons/Clear";
import {FloorPlan} from "../SharedTypes";
import {BackendContext} from "../components/BackendContext";
import {Avatar} from "@material-ui/core";
import {FloorPlanEditor} from "../components/FloorPlanEditor";
import FloorPlanOptionTable from "../components/FloorPlanOptionTable";
import Add from "@material-ui/icons/Add";
import CloudUpload from "@material-ui/icons/CloudUpload";
import swal2 from 'sweetalert2';
import LoadingModal from "../components/LoadingModal";
import {CloudDownload} from "@material-ui/icons";
import Sortable from 'sortablejs';

const useStyles = makeStyles(theme => ({
    table: {
        minWidth: 650,
    },
    container: {
        margin: 'auto',
        marginTop: theme.spacing(2),
        // maxWidth: theme.breakpoints.values.md,
    },
    formContainer: {
        margin: 'auto',
        marginTop: theme.spacing(4),
        maxWidth: theme.breakpoints.values.md,
        padding: theme.spacing(4),
    },
    infoColor: {
        color: theme.palette.info.dark
    },
    dangerColor: {
        color: theme.palette.error.dark
    },
    title: {
        flexGrow: 1,
    },
}));

export default function AdminListFloorPlans() {
    const classes = useStyles(),
        [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]),
        [editFloorPlan, setEditFloorPlan] = useState<FloorPlan | undefined>(),
        [editFloorPlanOptions, setEditFloorPlanOptions] = useState<FloorPlan | undefined>(),
        [showLoadingModal, setShowLoadingModal] = useState<string | undefined>(),
        backendApi = useContext(BackendContext),
        sortableRoot = useRef<HTMLElement>(null);

    function reloadFloorPlans() {
            Promise.all([
                backendApi.getFloorPlanOrder(),
                backendApi.listFloorPlans()
            ]).then(([order, floorPlans]) =>
                setFloorPlans(floorPlans.sort((a, b) =>
                    order.indexOf(parseInt(a.id as any)) - order.indexOf(parseInt(b.id as any)))));
    }

    useEffect(() => {
        Promise.all([
            backendApi.getFloorPlanOrder(),
            backendApi.listFloorPlans()
        ]).then(([order, floorPlans]) =>
            setFloorPlans(floorPlans.sort((a, b) =>
                order.indexOf(parseInt(a.id as any)) - order.indexOf(parseInt(b.id as any)))));
    }, [backendApi]);

    useEffect(() => {
        if (sortableRoot.current) {
            const tableRows = sortableRoot.current;
            const sortable = new Sortable(sortableRoot.current, {
                animation: 150,
                onEnd: (e) => {
                    const order = Array.from(tableRows.querySelectorAll('tr'))
                        .map(e => parseInt(e.getAttribute('data-id') || ''))
                        .filter(id => !!id);
                    backendApi.updateFloorPlanOrder(order).then(() => {
                        console.log("Order Saved");
                    }, e => console.error("Unable to save order", e));
                }
            });
            return () => {
                sortable.destroy();
            };
        }
    }, [backendApi, sortableRoot, floorPlans]);

    return (
        <>
            {editFloorPlanOptions
                ? <FloorPlanOptionTable floorPlan={editFloorPlanOptions} onChange={setEditFloorPlanOptions}
                                        onBack={() => setEditFloorPlanOptions(undefined)}
                                        onSave={async (newFloorPlan) => {
                                            // console.log("SAVING FLOOR PLAN OPTIONs", newFloorPlan);
                                            if (newFloorPlan?.id) {
                                                await backendApi.updateFloorPlan(newFloorPlan);
                                                reloadFloorPlans();
                                            }
                                        }}/>
                : editFloorPlan ? <>
                        <Paper className={classes.formContainer}>
                            <FloorPlanEditor floorPlan={editFloorPlan} onChange={setEditFloorPlan} onSave={async () => {
                                if (editFloorPlan?.id) {
                                    await backendApi.updateFloorPlan(editFloorPlan);
                                } else {
                                    await backendApi.createFloorPlan(editFloorPlan);
                                }
                                reloadFloorPlans();
                                setEditFloorPlan(undefined);
                            }} onBack={() => setEditFloorPlan(undefined)}/>
                        </Paper>
                    </>
                    : <TableContainer component={Paper} className={classes.container}>
                        <Typography component="h2" variant="h4" style={{margin: '1rem'}}>Floor Plans</Typography>
                        <Table className={classes.table} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell/>
                                    <TableCell component="th">Name</TableCell>
                                    <TableCell component="th">Community</TableCell>
                                    <TableCell component="th" align="right"/>
                                </TableRow>
                            </TableHead>
                            <TableBody ref={sortableRoot as any}>
                                {floorPlans.map(fp => <TableRow key={fp.name} data-id={fp.id}>
                                    <TableCell>
                                        <Avatar src={fp.photo?.url}/>
                                    </TableCell>
                                    <TableCell>
                                        {fp.name}
                                    </TableCell>
                                    <TableCell>{fp.community}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="primary" style={{marginRight: '1rem'}}
                                                onClick={() => setEditFloorPlanOptions(fp)}>
                                            Edit Options
                                        </Button>
                                        <Tooltip title="Export Floor Plan">
                                            <IconButton onClick={() => exportFloorPlan(fp)}>
                                                <CloudDownload className={classes.infoColor}/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Floor Plan">
                                            <IconButton onClick={() => setEditFloorPlan(fp)}>
                                                <Edit className={classes.infoColor}/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove Floor Plan">
                                            <IconButton onClick={async () => {
                                                await backendApi.deleteFloorPlan(fp.id);
                                                reloadFloorPlans();
                                            }}>
                                                <Clear className={classes.dangerColor}/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                        <Button color="primary" startIcon={<Add/>}
                                onClick={() => setEditFloorPlan({
                                    name: '',
                                    community: '',
                                    description: ''
                                } as FloorPlan)}>
                            Add Floor Plan
                        </Button>

                        <Button color="primary" startIcon={<CloudUpload/>}
                                onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.onchange = (e: any) => {
                                        const file = e.target.files[0],
                                            reader = new FileReader();
                                        reader.readAsText(file, 'UTF-8');
                                        reader.onload = async (readerEvent) => {
                                            try {
                                                setShowLoadingModal('Importing Floor Plan...');
                                                const content = JSON.parse(readerEvent.target?.result as string);
                                                await backendApi.createFloorPlan(content, true);
                                                await reloadFloorPlans();
                                                setShowLoadingModal(undefined);
                                                await swal2.fire({
                                                    title: 'Success',
                                                    icon: 'success',
                                                    text: `Floor Plan: ${content.name} has been imported!`,
                                                });
                                            } catch (e) {
                                                setShowLoadingModal(undefined);
                                                console.error(e);
                                                await swal2.fire({
                                                    title: 'Error',
                                                    icon: 'error',
                                                    text: 'Unable to upload'
                                                });
                                            }
                                        }
                                    }
                                    input.click();
                                }}>
                            Import Floor Plan
                        </Button>
                    </TableContainer>}
            <LoadingModal title={showLoadingModal}/>
        </>
    );
}

function exportFloorPlan(floorPlan: FloorPlan) {
    let link = document.createElement("a");
    let blob = new Blob([JSON.stringify(floorPlan, null, 4)]);
    link.href = URL.createObjectURL(blob);
    link.download = `${floorPlan.name.replace(/\s+/g, '_').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
